import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIsAdmin } from '@hooks/usePermission';

import { useEmployeeListRoster } from '../../../hooks/useEmployeeListRoster';
import {
  connectKeka,
  disconnectKeka,
  getKekaEmployees,
  autoMapKekaEmployees,
  saveKekaMappings,
  syncKekaTimeRange,
  subscribeToKekaStatus,
} from '../../../services/keka';
import {
  addNotification,
  notificationStates,
} from '../../../components/Notification';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import styles from './QuickBooksCard.module.scss';
import connectFormStyles from './KekaConnectForm.module.scss';

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function fmtDate(dateObj) {
  if (!dateObj) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(dateObj);
}

const EMPTY_FORM = {
  kekaCompanySlug: '',
  clientId: '',
  clientSecret: '',
  apiKey: '',
};

export const KekaCard = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );

  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const isAdmin = useIsAdmin();
  const teamId = activeTeamId;

  const { employees: prodaffEmployees, isLoading: rosterLoading } =
    useEmployeeListRoster(teamId);

  const [kekaStatus, setKekaStatus] = useState({
    connected: false,
    kekaCompanySlug: null,
    connectedAt: null,
    connectedBy: null,
    employeeMappings: {},
    employeeMappingsUpdatedAt: null,
    lastSyncAt: null,
    lastSyncStats: null,
  });

  const [kekaEmployees, setKekaEmployees] = useState([]);
  const [kekaEmployeesLoading, setKekaEmployeesLoading] = useState(false);
  const [pendingMappings, setPendingMappings] = useState({});

  const [connectForm, setConnectForm] = useState(EMPTY_FORM);
  const [showSecrets, setShowSecrets] = useState(false);

  const [syncRange, setSyncRange] = useState({
    start: firstOfMonthStr(),
    end: todayStr(),
  });
  const [syncing, setSyncing] = useState(false);

  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);

  // ── Firestore real-time status ──────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const unsub = subscribeToKekaStatus(companyId, setKekaStatus);
    return unsub;
  }, [companyId]);

  // ── Load Keka employees when connected ─────────────────────────────────────
  useEffect(() => {
    if (!kekaStatus.connected || !companyId) {
      setKekaEmployees([]);
      return;
    }
    setKekaEmployeesLoading(true);
    getKekaEmployees(companyId)
      .then(({ employees }) => setKekaEmployees(employees || []))
      .catch(() =>
        addNotification({
          message: 'Failed to load Keka employees.',
          level: notificationStates.error,
        })
      )
      .finally(() => setKekaEmployeesLoading(false));
  }, [kekaStatus.connected, companyId]);

  const effectiveMappings = useMemo(() => {
    const base = kekaStatus.employeeMappings || {};
    const merged = { ...base };
    for (const [uid, val] of Object.entries(pendingMappings)) {
      if (val === null) delete merged[uid];
      else merged[uid] = val;
    }
    return merged;
  }, [kekaStatus.employeeMappings, pendingMappings]);

  const hasPendingChanges = Object.keys(pendingMappings).length > 0;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleConnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can connect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    const { kekaCompanySlug, clientId, clientSecret, apiKey } = connectForm;
    if (
      !kekaCompanySlug.trim() ||
      !clientId.trim() ||
      !clientSecret.trim() ||
      !apiKey.trim()
    ) {
      addNotification({
        message: 'Please fill in all fields.',
        level: notificationStates.error,
      });
      return;
    }
    setConnecting(true);
    try {
      await connectKeka(
        companyId,
        userId,
        kekaCompanySlug.trim(),
        clientId.trim(),
        clientSecret.trim(),
        apiKey.trim()
      );
      setConnectForm(EMPTY_FORM);
      addNotification({
        message: 'Keka connected successfully.',
        level: notificationStates.success,
      });
    } catch (err) {
      addNotification({
        message:
          err?.response?.data?.message ||
          'Failed to connect to Keka. Please check your credentials.',
        level: notificationStates.error,
      });
    } finally {
      setConnecting(false);
    }
  }, [companyId, userId, isAdmin, connectForm]);

  const handleDisconnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can disconnect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setDisconnecting(true);
    try {
      await disconnectKeka(companyId, userId);
      setPendingMappings({});
      addNotification({
        message: 'Keka disconnected.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to disconnect Keka. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setDisconnecting(false);
    }
  }, [companyId, userId, isAdmin]);

  const handleAutoMap = useCallback(async () => {
    setAutoMapping(true);
    try {
      const { mapped, unmatched } = await autoMapKekaEmployees(
        companyId,
        teamId
      );
      setPendingMappings({});
      const total = mapped + (unmatched?.length ?? 0);
      addNotification({
        message: `Auto-mapped ${mapped} of ${total} employees by email.`,
        level:
          mapped > 0 ? notificationStates.success : notificationStates.warning,
      });
    } catch {
      addNotification({
        message: 'Auto-mapping failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setAutoMapping(false);
    }
  }, [companyId, teamId]);

  const handleSelectMapping = useCallback(
    (prodaffUserId, kekaEmployeeId) => {
      if (!kekaEmployeeId) {
        setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
        return;
      }
      const emp = kekaEmployees.find((e) => e.id === kekaEmployeeId);
      if (!emp) return;
      setPendingMappings((prev) => ({
        ...prev,
        [prodaffUserId]: {
          kekaEmployeeId: emp.id,
          kekaEmployeeName: emp.name,
          kekaEmployeeNumber: emp.employeeNumber || null,
        },
      }));
    },
    [kekaEmployees]
  );

  const handleUnmap = useCallback((prodaffUserId) => {
    setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
  }, []);

  const handleSaveMappings = useCallback(async () => {
    if (!hasPendingChanges) return;
    setSavingMappings(true);
    try {
      await saveKekaMappings(companyId, pendingMappings);
      setPendingMappings({});
      addNotification({
        message: 'Employee mappings saved.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to save mappings. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSavingMappings(false);
    }
  }, [companyId, pendingMappings, hasPendingChanges]);

  const handleSync = useCallback(async () => {
    if (!syncRange.start || !syncRange.end) {
      addNotification({
        message: 'Please select a valid date range.',
        level: notificationStates.error,
      });
      return;
    }
    setSyncing(true);
    try {
      const result = await syncKekaTimeRange(
        companyId,
        teamId,
        syncRange.start,
        syncRange.end
      );
      addNotification({
        message: `Sync complete. ${result.synced ?? 0} records synced.`,
        level: notificationStates.success,
      });
    } catch (err) {
      addNotification({
        message:
          err?.response?.data?.message || 'Sync failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSyncing(false);
    }
  }, [companyId, teamId, syncRange]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Connection status panel */}
      <div className={styles.statusPanel}>
        <div className={styles.statusLeft}>
          <span
            className={`${styles.badge} ${kekaStatus.connected ? styles.connected : styles.disconnected}`}
          >
            {kekaStatus.connected ? 'Connected' : 'Not Connected'}
          </span>
          {kekaStatus.connected ? (
            <span className={styles.connectedMeta}>
              {kekaStatus.kekaCompanySlug &&
                `Workspace: ${kekaStatus.kekaCompanySlug}`}
              {kekaStatus.connectedAt &&
                ` · Connected ${fmtDate(kekaStatus.connectedAt)}`}
            </span>
          ) : (
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Connect your Keka account to start syncing attendance data.
            </Text>
          )}
        </div>
        {isAdmin && kekaStatus.connected && (
          <Button
            text={disconnecting ? 'Disconnecting…' : 'Disconnect'}
            variant={buttonVariants.secondary}
            size={buttonSizes.sm}
            onClick={handleDisconnect}
            disabled={disconnecting}
          />
        )}
      </div>

      {/* Connect form — shown to admins when not connected */}
      {!kekaStatus.connected && isAdmin && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Connect Keka</span>
              <span className={styles.sectionSubtitle}>
                Enter your Keka API credentials from your Keka admin panel.
              </span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            <div className={connectFormStyles.form}>
              <div className={connectFormStyles.field}>
                <label htmlFor="keka-slug">Company Slug</label>
                <input
                  id="keka-slug"
                  type="text"
                  placeholder="e.g. acmecorp"
                  value={connectForm.kekaCompanySlug}
                  onChange={(e) =>
                    setConnectForm((f) => ({
                      ...f,
                      kekaCompanySlug: e.target.value,
                    }))
                  }
                  disabled={connecting}
                  autoComplete="off"
                />
                <span className={connectFormStyles.hint}>
                  The subdomain of your Keka workspace (acmecorp.keka.com)
                </span>
              </div>
              <div className={connectFormStyles.field}>
                <label htmlFor="keka-client-id">Client ID</label>
                <input
                  id="keka-client-id"
                  type="text"
                  placeholder="Client ID from Keka API settings"
                  value={connectForm.clientId}
                  onChange={(e) =>
                    setConnectForm((f) => ({ ...f, clientId: e.target.value }))
                  }
                  disabled={connecting}
                  autoComplete="off"
                />
              </div>
              <div className={connectFormStyles.field}>
                <label htmlFor="keka-client-secret">
                  Client Secret
                  <button
                    type="button"
                    className={connectFormStyles.toggleSecret}
                    onClick={() => setShowSecrets((v) => !v)}
                  >
                    {showSecrets ? 'Hide' : 'Show'}
                  </button>
                </label>
                <input
                  id="keka-client-secret"
                  type={showSecrets ? 'text' : 'password'}
                  placeholder="Client Secret from Keka API settings"
                  value={connectForm.clientSecret}
                  onChange={(e) =>
                    setConnectForm((f) => ({
                      ...f,
                      clientSecret: e.target.value,
                    }))
                  }
                  disabled={connecting}
                  autoComplete="new-password"
                />
              </div>
              <div className={connectFormStyles.field}>
                <label htmlFor="keka-api-key">API Key</label>
                <input
                  id="keka-api-key"
                  type={showSecrets ? 'text' : 'password'}
                  placeholder="API Key from Keka API settings"
                  value={connectForm.apiKey}
                  onChange={(e) =>
                    setConnectForm((f) => ({ ...f, apiKey: e.target.value }))
                  }
                  disabled={connecting}
                  autoComplete="new-password"
                />
              </div>
              <div className={connectFormStyles.actions}>
                <Button
                  text={connecting ? 'Connecting…' : 'Connect Keka'}
                  variant={buttonVariants.primary}
                  size={buttonSizes.sm}
                  onClick={handleConnect}
                  disabled={connecting}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee Mapping panel */}
      {kekaStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Employee Mapping</span>
              <span className={styles.sectionSubtitle}>
                Map Prodaff employees to their Keka employee records.
              </span>
            </div>
            <div className={styles.sectionActions}>
              <Button
                text={autoMapping ? 'Mapping…' : 'Auto-map by Email'}
                variant={buttonVariants.secondary}
                size={buttonSizes.sm}
                onClick={handleAutoMap}
                disabled={autoMapping || kekaEmployeesLoading}
              />
            </div>
          </div>

          <div className={styles.sectionBody}>
            {rosterLoading || kekaEmployeesLoading ? (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                color="gray-500"
              >
                Loading…
              </Text>
            ) : prodaffEmployees.length === 0 ? (
              <EmptyState
                illustration
                title="No Employees Found"
                description="No employees found in this department to map to Keka."
              />
            ) : (
              <>
                <table className={styles.mappingTable}>
                  <thead>
                    <tr>
                      <th>Prodaff Employee</th>
                      <th>Keka Employee</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prodaffEmployees.map((emp) => {
                      const mapping = effectiveMappings[emp.userId];
                      return (
                        <tr key={emp.userId}>
                          <td>
                            <div className={styles.employeeName}>
                              {emp.name}
                            </div>
                            <div className={styles.employeeEmail}>
                              {emp.email}
                            </div>
                          </td>
                          <td>
                            {mapping ? (
                              <div className={styles.mappingCell}>
                                <span className={styles.mappedTag}>
                                  {mapping.kekaEmployeeName ||
                                    mapping.kekaEmployeeId}
                                  {mapping.kekaEmployeeNumber && (
                                    <span
                                      style={{
                                        color: '#60a5fa',
                                        fontWeight: 400,
                                      }}
                                    >
                                      {' '}
                                      #{mapping.kekaEmployeeNumber}
                                    </span>
                                  )}
                                  <button
                                    className={styles.unmapBtn}
                                    onClick={() => handleUnmap(emp.userId)}
                                    title="Remove mapping"
                                  >
                                    ×
                                  </button>
                                </span>
                              </div>
                            ) : (
                              <select
                                className={styles.qbSelect}
                                value=""
                                onChange={(e) =>
                                  handleSelectMapping(
                                    emp.userId,
                                    e.target.value
                                  )
                                }
                                disabled={kekaEmployees.length === 0}
                              >
                                <option value="">
                                  — Select Keka employee —
                                </option>
                                {kekaEmployees.map((e) => (
                                  <option key={e.id} value={e.id}>
                                    {e.name}
                                    {e.employeeNumber
                                      ? ` (#${e.employeeNumber})`
                                      : ''}
                                    {e.email ? ` · ${e.email}` : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            <div className={styles.actionCell} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className={styles.saveMappingsRow}>
                  {hasPendingChanges && (
                    <span className={styles.pendingNote}>Unsaved changes</span>
                  )}
                  <Button
                    text={savingMappings ? 'Saving…' : 'Save Mappings'}
                    variant={buttonVariants.primary}
                    size={buttonSizes.sm}
                    onClick={handleSaveMappings}
                    disabled={savingMappings || !hasPendingChanges}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sync panel */}
      {kekaStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Sync Attendance</span>
              <span className={styles.sectionSubtitle}>
                Push Prodaff tracked hours to Keka as attendance punch records
                for a selected date range.
              </span>
            </div>
          </div>

          <div className={styles.sectionBody}>
            <div className={styles.syncRow}>
              <div className={styles.dateField}>
                <label htmlFor="keka-start-date">From</label>
                <input
                  id="keka-start-date"
                  type="date"
                  value={syncRange.start}
                  max={syncRange.end || todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, start: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <div className={styles.dateField}>
                <label htmlFor="keka-end-date">To</label>
                <input
                  id="keka-end-date"
                  type="date"
                  value={syncRange.end}
                  min={syncRange.start}
                  max={todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, end: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <Button
                text={syncing ? 'Syncing…' : 'Sync Now'}
                variant={buttonVariants.primary}
                size={buttonSizes.sm}
                onClick={handleSync}
                disabled={syncing || !syncRange.start || !syncRange.end}
              />
            </div>

            {kekaStatus.lastSyncAt && (
              <div className={styles.syncStats}>
                <div className={styles.syncStatsTitle}>
                  Last sync · {fmtDate(kekaStatus.lastSyncAt)}
                  {kekaStatus.lastSyncStats?.startDate &&
                    kekaStatus.lastSyncStats?.endDate && (
                      <span style={{ fontWeight: 400, color: '#64748b' }}>
                        {' '}
                        ({kekaStatus.lastSyncStats.startDate} →{' '}
                        {kekaStatus.lastSyncStats.endDate})
                      </span>
                    )}
                </div>
                {kekaStatus.lastSyncStats && (
                  <div className={styles.syncStatsGrid}>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Synced</span>
                      <span className={`${styles.statValue} ${styles.success}`}>
                        {kekaStatus.lastSyncStats.synced ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Skipped</span>
                      <span className={`${styles.statValue} ${styles.warning}`}>
                        {kekaStatus.lastSyncStats.skipped ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Errors</span>
                      <span className={`${styles.statValue} ${styles.error}`}>
                        {kekaStatus.lastSyncStats.errors ?? 0}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
